"use client";

import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { ChevronUp, Search, X } from "lucide-react";
import MerchantLayout, { MenuSectionComponent } from "./MerchantLayout";
import UniversalButton from "@/components/common/UniversalButton";
import { Button } from "@/components/ui/button";
import { useInfiniteQuery } from "@tanstack/react-query";
import { getMenu, StorefrontMenu, uniqueMenus } from "@/services/api/product/menu";
import { useInView } from "react-intersection-observer";
import SearchInput from "@/components/ui/search";
import { SEARCH_PLACEHOLDER } from "@/constants/product/search";
import Sort from "@/components/filter-panel/sort";
import ResponsiveSortFilter from "./ResponsiveSortFilter";
interface CakeCoustamizationProps {
  vendorId: string;
  vendor_offline: boolean;
  initialProducts?: Record<string, unknown>[];
  vendorSlug?: string;
  initialMenus?: StorefrontMenu[];
}
export default function CakeCoustamization({
  vendorId,
  vendor_offline,
  initialProducts,
  vendorSlug,
  initialMenus
}: CakeCoustamizationProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [sectionRefs, setSectionRefs] = useState<Map<string, React.RefObject<HTMLDivElement>>>(new Map());
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [showMobileSort, setShowMobileSort] = useState(false);
  const componentRef = useRef<HTMLDivElement>(null);
  const fallbackFromProducts = useMemo(() => {
    if (!initialProducts || initialProducts.length === 0) return [];
    const sectionMap = new Map<number, StorefrontMenu>();
    for (const p of initialProducts as Array<{
      menu?: {
        id?: number;
        name?: string;
      };
    }>) {
      if (p.menu && p.menu.id) {
        if (!sectionMap.has(p.menu.id)) {
          sectionMap.set(p.menu.id, {
            id: p.menu.id,
            name: p.menu.name || "Products",
            product_count: 0
          });
        }
        const s = sectionMap.get(p.menu.id)!;
        s.product_count = (s.product_count || 0) + 1;
      }
    }
    return Array.from(sectionMap.values());
  }, [initialProducts]);
  const effectiveInitialMenus = useMemo(() => {
    if (initialMenus && initialMenus.length > 0) return initialMenus;
    return fallbackFromProducts;
  }, [initialMenus, fallbackFromProducts]);
  const {
    ref: menuRef,
    inView: menuInView
  } = useInView({
    threshold: 0.1,
    triggerOnce: false
  });
  const {
    data: menuData,
    isLoading: isMenuLoading,
    isError: isMenuError,
    isFetchingNextPage: isFetchingNextMenuPage,
    hasNextPage: hasNextMenuPage,
    fetchNextPage: fetchNextMenuPage
  } = useInfiniteQuery({
    queryKey: ["menu", vendorId],
    queryFn: ({
      pageParam = 1
    }) => getMenu({
      page: pageParam,
      vendor_id: vendorId
    }),
    initialData: initialMenus && initialMenus.length > 0 ? {
      pages: [{
        results: initialMenus,
        count: initialMenus.length,
        next: null,
        previous: null
      }],
      pageParams: [1]
    } : undefined,
    getNextPageParam: lastPage => {
      if (lastPage?.next) {
        try {
          const url = new URL(lastPage.next);
          const pageParam_0 = url.searchParams.get("page");
          return pageParam_0 ? parseInt(pageParam_0) : undefined;
        } catch {
          return undefined;
        }
      }
      return undefined;
    },
    initialPageParam: 1,
    enabled: !!vendorId,
    staleTime: 60 * 1000
  });
  useEffect(() => {
    if (menuInView && hasNextMenuPage && !isFetchingNextMenuPage) {
      fetchNextMenuPage();
    }
  }, [menuInView, hasNextMenuPage, isFetchingNextMenuPage, fetchNextMenuPage]);
  const allMenuSections = useMemo(() => {
    const fromQuery = menuData?.pages?.flatMap(page => page.results) || [];
    if (fromQuery.length > 0) return uniqueMenus(fromQuery);
    if (effectiveInitialMenus && effectiveInitialMenus.length > 0) {
      return uniqueMenus(effectiveInitialMenus);
    }
    return [];
  }, [menuData, effectiveInitialMenus]);
  const sectionsWithProducts = useMemo(() => allMenuSections.filter(section => (section.product_count ?? 1) > 0), [allMenuSections]);
  const sectionsToShow = useMemo(() => {
    return sectionsWithProducts.length > 0 ? sectionsWithProducts : allMenuSections;
  }, [sectionsWithProducts, allMenuSections]);
  const [activeSection, setActiveSection] = useState<string>("");
  useEffect(() => {
    if (!activeSection && sectionsToShow.length > 0 && sectionsToShow[0]) {
      setActiveSection(sectionsToShow[0].name);
    }
  }, [activeSection, sectionsToShow]);
  useEffect(() => {
    const handleScroll = () => {
      if (componentRef.current) {
        const componentTop = componentRef.current.offsetTop;
        const scrollTop = window.scrollY;
        setShowBackToTop(scrollTop > componentTop + 300);
        setShowMobileSort(componentRef.current.getBoundingClientRect().top <= 80);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  const scrollToTop = useCallback(() => {
    if (componentRef.current) {
      componentRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    }
  }, []);
  const handleSectionRef = useCallback((sectionName: string, ref: React.RefObject<HTMLDivElement>) => {
    setSectionRefs(prev => new Map(prev).set(sectionName, ref));
  }, []);
  const scrollToSection = useCallback((sectionName_0: string) => {
    setActiveSection(sectionName_0);
    const sectionRef = sectionRefs.get(sectionName_0);
    if (sectionRef?.current) {
      sectionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    }
  }, [sectionRefs]);
  const categoryButtons = useMemo(() => {
    const justifyClassMobile = sectionsToShow.length <= 2 ? "justify-center" : "justify-start";
    const justifyClassDesktop = sectionsToShow.length <= 7 ? "md:justify-center" : "md:justify-start";
    return <div className={`flex flex-row ${justifyClassMobile} ${justifyClassDesktop} items-center gap-2 px-4 overflow-x-auto w-full md:w-auto pb-2 md:pb-0 no-scrollbar`}>
        {sectionsToShow.map(section_0 => {
        const isActive = activeSection === section_0.name;
        const baseClasses = "px-4 py-2 rounded-xl border font-outfit text-base font-medium uppercase whitespace-nowrap";
        const activeClasses = "border-v2-primary bg-v2-primary/5 text-v2-primary";
        const inactiveClasses = "border-v2-outline-variant/50 text-v2-on-surface-variant hover:border-v2-primary hover:text-v2-primary transition-colors";
        return <UniversalButton key={section_0.id} onClick={() => scrollToSection(section_0.name)} variant="custom" classNames={`${baseClasses} ${isActive ? activeClasses : inactiveClasses}`} text={section_0.name} ariaLabel={`Scroll to ${section_0.name} section`} />;
      })}
      </div>;
  }, [sectionsToShow, scrollToSection, activeSection]);
  const loadingView = useMemo(() => <div className="min-h-[200px] flex items-center justify-center">
        <div className="text-lg font-outfit text-[#3E3F41]">
          Loading menu...
        </div>
      </div>, []);
  const errorView = useMemo(() => <div className="min-h-[200px] flex items-center justify-center">
        <div className="text-lg font-outfit text-[#3E3F41]">
          Error loading menu
        </div>
      </div>, []);
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  }, []);
  const handleClearSearch = useCallback(() => {
    setSearchQuery("");
  }, []);
  const menuContent = useMemo(() => <>
        <MerchantLayout menuSections={sectionsToShow} onSectionRef={handleSectionRef} vendorId={vendorId} initialProducts={initialProducts} vendorSlug={vendorSlug} vendor_offline={vendor_offline} />
        {isFetchingNextMenuPage && <div className="flex justify-center py-4">
            <div className="text-sm font-outfit text-[#3E3F41]">
              Loading more menu sections...
            </div>
          </div>}
        {hasNextMenuPage && <div ref={menuRef} className="h-4" />}
      </>, [sectionsToShow, handleSectionRef, vendorId, initialProducts, vendorSlug, vendor_offline, isFetchingNextMenuPage, hasNextMenuPage, menuRef]);
  const backToTopButton = useMemo(() => showBackToTop && <Button variant="ghost" size="icon" onClick={scrollToTop} className="fixed bottom-6 right-6 z-30 bg-[#079ea6] hover:bg-[#068a91] text-white rounded-full p-3 shadow-lg transition-all duration-300 hover:scale-110" aria-label="Back to top">
          <ChevronUp size={24} />
        </Button>, [showBackToTop, scrollToTop]);
  return <>
      <div ref={componentRef} className="max-w-[1440px] mx-auto lg:pt-14 lg:px-8 px-2">
        <h2 className="text-4xl max-sm:text-xl font-bold max-sm:font-bold mb-2 sm:mb-2 font-serif justify-center flex items-center text-gray-600 max-sm:tracking-[1px] max-sm:whitespace-nowrap uppercase">
          <span className="block tracking-[-2%] leading-[100%] mb-8">
            Our Products
          </span>
        </h2>
        <div className="flex flex-col lg:flex-row justify-between items-center w-full mb-6 lg:mb-9 gap-4">
          <div className="relative shadow-sm w-full lg:w-[400px] border border-[#DFDEDE] rounded-md flex items-center">
            <div className="absolute inset-y-0 left-3 flex items-center ">
              <Search className="text-[#767676]  font-normal" strokeWidth={1.15} />
            </div>
            <SearchInput type="text" placeholder={SEARCH_PLACEHOLDER} className="text-sm md:text-base lg:text-lg text-[#37373C] font-normal p-[6px] pl-10 pr-10 w-full md:p-3 md:pl-12 md:pr-12 bg-white rounded-full border-none focus:outline-none h-12" value={searchQuery} onChange={handleSearchChange} />
            {searchQuery && <Button variant="ghost" size="icon" onClick={handleClearSearch} className="absolute top-1/2 -translate-y-1/2 right-2 flex items-center justify-center text-[#767676] hover:text-[#37373C] transition-colors" aria-label="Clear search">
                <X size={20} strokeWidth={1.5} />
              </Button>}
          </div>
          <div className="hidden lg:block ml-auto">
            <Sort />
          </div>
        </div>
      </div>

      <div className="max-w-[1440px] mx-auto lg:px-8 px-2 relative">
        {!searchQuery && <div className="sticky top-0 z-40 bg-white py-2 md:py-3 mb-5 md:mb-9 shadow-sm">
            {categoryButtons}
          </div>}
        <div className="md:mb-12">
          {isMenuLoading && sectionsToShow.length === 0 ? loadingView : isMenuError && sectionsToShow.length === 0 ? errorView : searchQuery ? <MenuSectionComponent searchQuery={searchQuery} vendorId={vendorId} vendor_offline={vendor_offline} vendorSlug={vendorSlug} /> : menuContent}
        </div>
      </div>
      {backToTopButton}
      <ResponsiveSortFilter isVisible={showMobileSort} onSortSelect={scrollToTop} />
    </>;
}